import {
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    PageContainer,
    SelectBox,
    TextInput,
    Switch,
    Dialog,
    Button,
    Text
} from "ncore-ui-kit";
import {
    useNavigation,
    useRoute
} from "@react-navigation/native";
import type RootStackParamList from "../../../../navigation/type";
import type {
    FormCreateForm
} from "../create/type";
import type {
    FormFieldItem
} from "../list/type";
import type {
    RouteProp
} from "@react-navigation/native";
import {
    ArrowLeftIcon,
    TrashIcon,
    PlusIcon,
    SaveIcon
} from "lucide-react-native";
import {
    type NativeStackNavigationProp
} from "@react-navigation/native-stack";
import RESTServiceContext from "../../../../services/restAPI/context";

type RouteParams = RouteProp<RootStackParamList, "FormEdit">;

const FormEdit = () => {
    const route = useRoute<RouteParams>();
    const formID = route.params?.formID;
    const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    const [
        saving,
        setSaving
    ] = useState<boolean>(false);

    const [
        isDialogVisible,
        setIsDialogVisible
    ] = useState<boolean>(false);

    const [
        isDeleteDialogVisible,
        setIsDeleteDialogVisible
    ] = useState<boolean>(false);

    const [
        formData,
        setFormData
    ] = useState<FormCreateForm>({
        isOnlyMembers: false,
        description: "",
        fields: [],
        title: ""
    });

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    useEffect(() => {
        if(!formID) {
            navigation.goBack();
            return;
        }
        const fetchForm = async () => {
            try {
                setLoading(true);
                const result = await RESTServiceContext.action("getFormForAdmin", {
                    formID: formID as string
                });
                const typedResult = result as { payload?: FormCreateForm };
                if(typedResult.payload) {
                    setFormData({
                        isOnlyMembers: typedResult.payload.isOnlyMembers || false,
                        description: typedResult.payload.description || "",
                        fields: typedResult.payload.fields || [],
                        title: typedResult.payload.title || ""
                    });
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        if (route.params?.formID) {
            fetchForm();
        }
    }, [
        formID,
        route.params?.formID
    ]);

    const handleSave = async () => {
        if(!formID) {
            return;
        }

        const keys = formData.fields.map(f => f.key).filter(k => k.trim() !== "");
        const uniqueKeys = new Set(keys);
        if (keys.length !== uniqueKeys.size) {
            alert(localize("forms-create-duplicate-key-error"));
            return;
        }
        if (keys.length !== formData.fields.length) {
            alert(localize("forms-create-empty-key-error"));
            return;
        }

        setIsDialogVisible(true);
    };

    const handleConfirmSave = async () => {
        setIsDialogVisible(false);
        try {
            setSaving(true);

            const payloadFields = formData.fields.map(field => ({
                maxLength: field.type === "yes_no" || field.type === "selectbox" ? undefined : (field.maxLength || 250),
                minLength: field.type === "yes_no" || field.type === "selectbox" ? undefined : (field.minLength || 0),
                options: field.type === "selectbox" ? field.options?.map(opt => ({
                    title: opt.title,
                    key: opt.key
                })) : undefined,
                title: field.title,
                type: field.type,
                key: field.key
            }));

            const result = await RESTServiceContext.action("editForm", {
                ...formData,
                formID: formID as string,
                fields: payloadFields
            });
            const typedResult = result as { code?: number; message?: string };
            if(typedResult.code === 200 || typedResult.code === 201) {
                navigation.goBack();
            } else {
                console.error(typedResult.message);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if(!formID) {
            return;
        }

        setIsDeleteDialogVisible(true);
    };

    const handleConfirmDelete = async () => {
        setIsDeleteDialogVisible(false);
        try {
            setSaving(true);
            const result = await RESTServiceContext.action("deleteForm", {
                formID: formID as string
            });
            const typedResult = result as { code?: number; message?: string };
            if(typedResult.code === 200 || typedResult.code === 201) {
                navigation.goBack();
            } else {
                console.error(typedResult.message);
            }
        } catch (error) {
            console.error(error);
        } finally {
            setSaving(false);
        }
    };

    const addField = () => {
        setFormData(prev => ({
            ...prev,
            fields: [
                ...prev.fields,
                {
                    type: "string",
                    title: "",
                    key: ""
                }
            ]
        }));
    };

    const updateField = (
        index: number,
        fieldData: Partial<FormFieldItem>
    ) => {
        setFormData(prev => {
            const newFields = [...prev.fields];
            const updated = {
                ...newFields[index],
                ...fieldData
            };
            newFields[index] = updated as FormFieldItem;
            return {
                ...prev,
                fields: newFields
            };
        });
    };

    const removeField = (index: number) => {
        setFormData(prev => {
            const newFields = [...prev.fields];
            newFields.splice(index, 1);
            return {
                ...prev,
                fields: newFields
            };
        });
    };

    const addOptionToField = (fieldIndex: number) => {
        setFormData(prev => {
            const newFields = [...prev.fields];
            const currentField = newFields[fieldIndex];

            if(currentField) {
                const options = currentField.options || [];
                const newOption = {
                    title: "",
                    key: ""
                };
                newFields[fieldIndex] = {
                    ...currentField,
                    options: [
                        ...options,
                        newOption
                    ]
                };
            }
            return {
                ...prev,
                fields: newFields
            };
        });
    };

    const updateOptionInField = (
        fieldIndex: number,
        optionIndex: number,
        keyType: "title" | "key",
        value: string
    ) => {
        setFormData(prev => {
            const newFields = [...prev.fields];
            const currentField = newFields[fieldIndex];

            if(currentField && currentField.options) {
                const newOptions = [...currentField.options];
                const opt = newOptions[optionIndex];
                if(opt) {
                    newOptions[optionIndex] = {
                        ...opt,
                        [keyType]: value
                    };
                }
                newFields[fieldIndex] = {
                    ...currentField,
                    options: newOptions
                };
            }
            return {
                ...prev,
                fields: newFields
            };
        });
    };

    const removeOptionFromField = (
        fieldIndex: number,
        optionIndex: number
    ) => {
        setFormData(prev => {
            const newFields = [...prev.fields];
            const currentField = newFields[fieldIndex];

            if(currentField && currentField.options) {
                const newOptions = [...currentField.options];
                newOptions.splice(optionIndex, 1);
                newFields[fieldIndex] = {
                    ...currentField,
                    options: newOptions
                };
            }
            return {
                ...prev,
                fields: newFields
            };
        });
    };

    const fetchStaticDataAndFill = async (
        fieldIndex: number,
        actionName: "getSectors" | "getPositions"
    ) => {
        try {
            setLoading(true);
            const response = await RESTServiceContext.action(actionName, null);
            const typedResponse = response as { payload?: { key: string; value: string }[] };
            if(typedResponse && typedResponse.payload) {
                const options = typedResponse.payload.map(item => ({
                    title: item.value,
                    key: item.key
                }));
                setFormData(prev => {
                    const newFields = [...prev.fields];
                    const currentField = newFields[fieldIndex];
                    if(currentField) {
                        newFields[fieldIndex] = {
                            ...currentField,
                            options
                        };
                    }
                    return {
                        ...prev,
                        fields: newFields
                    };
                });
            }
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    const clearOptions = (fieldIndex: number) => {
        setFormData(prev => {
            const newFields = [...prev.fields];
            const currentField = newFields[fieldIndex];
            if(currentField) {
                newFields[fieldIndex] = {
                    ...currentField,
                    options: []
                };
            }
            return {
                ...prev,
                fields: newFields
            };
        });
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    marginBottom: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.headerBackContainer,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Button
                    icon={() => <ArrowLeftIcon
                        color={colors.project.brandWhite}
                        size={24}
                    />}
                    customBorderColor="secondaryButton"
                    onPress={() => navigation.goBack()}
                    customColor="secondaryButton"
                    spreadBehaviour="free"
                    variant="filled"
                />
                <Text
                    variant="titleLargeSize"
                >
                    {localize("forms-edit-title")}
                </Text>
            </View>
        </View>;
    };

    const renderGeneralInfo = () => {
        return <View
            style={[
                stylesheet.formGroup,
                {
                    gap: spaces.spacingLg
                }
            ]}
        >
            <Text
                variant="titleMediumSize"
            >
                {localize("forms-create-general-info")}
            </Text>
            <TextInput
                placeholder={localize("forms-create-form-title-placeholder")}
                title={localize("forms-create-form-title-input")}
                customColor={colors.project.inputBackground}
                onChangeText={text => setFormData(prev => ({
                    ...prev,
                    title: text
                }))}
                spreadBehaviour="stretch"
                value={formData.title}
                isRequired={true}
            />
            <TextInput
                placeholder={localize("forms-create-form-desc-placeholder")}
                title={localize("forms-create-form-desc-input")}
                customColor={colors.project.inputBackground}
                onChangeText={text => setFormData(prev => ({
                    ...prev,
                    description: text
                }))}
                value={formData.description}
                spreadBehaviour="stretch"
                multiline={true}
            />
            <View
                style={[
                    stylesheet.rowGroup,
                    {
                        gap: spaces.spacingMd
                    }
                ]}
            >
                <Switch
                    onPress={() => setFormData(prev => ({
                        ...prev,
                        isOnlyMembers: !prev.isOnlyMembers
                    }))}
                    customTheme={{
                        themeKey: "events_form" as never
                    }}
                    isActive={formData.isOnlyMembers}
                    type="neutral"
                />
                <View
                    style={{
                        flexDirection: "column",
                        flex: 1
                    }}
                >
                    <Text
                        variant="bodyMediumSize"
                    >
                        {localize("forms-create-only-members")}
                    </Text>
                    <Text
                        customColor="content.text.low"
                        variant="bodySmallSize"
                    >
                        {localize("forms-create-only-members-desc")}
                    </Text>
                </View>
            </View>
        </View>;
    };

    const renderField = (
        field: FormFieldItem,
        index: number
    ) => {
        const isDefault = field.key?.startsWith("default_");

        return <View
            style={[
                stylesheet.fieldContainer,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.project.brandBorder,
                    borderWidth: borders.seperator,
                    opacity: isDefault ? 0.7 : 1,
                    marginTop: spaces.spacingLg,
                    borderRadius: radiuses.md,
                    padding: spaces.spacingLg
                }
            ]}
            pointerEvents={isDefault ? "none" : "auto"}
            key={`field_${index}`}
        >
            <View
                style={stylesheet.fieldHeader}
            >
                <Text
                    variant="titleMediumSize"
                >
                    {localize("forms-create-question-prefix")} {index + 1} {isDefault && "(Sistem Alanı)"}
                </Text>
                {
                    !isDefault ?
                        <Button
                            icon={({
                                color,
                                size
                            }) => <TrashIcon
                                color={colors.content.icon[color]}
                                size={size}
                            />}
                            onPress={() => removeField(index)}
                            spreadBehaviour="free"
                            type="danger"
                        />
                    :
                        null
                }
            </View>
            <View
                style={[
                    stylesheet.fieldBody,
                    {
                        marginTop: spaces.spacingLg,
                        gap: spaces.spacingLg
                    }
                ]}
            >
                <TextInput
                    placeholder={localize("form-question-title-placeholder")}
                    title={localize("forms-create-question-title")}
                    customColor={colors.project.inputBackground}
                    onChangeText={text => updateField(index, {
                        title: text
                    })}
                    spreadBehaviour="stretch"
                    value={field.title}
                    isRequired={true}
                />
                <TextInput
                    placeholder={localize("forms-create-question-key-placeholder")}
                    title={localize("forms-create-question-key")}
                    customColor={colors.project.inputBackground}
                    onChangeText={text => updateField(index, {
                        key: text
                    })}
                    spreadBehaviour="stretch"
                    value={field.key}
                    isRequired={true}
                />
                <SelectBox
                    onChange={selected => {
                        const item = selected[0];
                        if (item) {
                            updateField(index, {
                                type: item.__key as FormFieldItem["type"]
                            });
                        }
                    }}
                    initialSelectedItems={[
                        {
                            __title: localize(`form-type-${field.type}`),
                            __key: field.type
                        }
                    ]}
                    data={[
                        {
                            title: localize("form-type-string"),
                            value: "string"
                        },
                        {
                            title: localize("form-type-longText"),
                            value: "longText"
                        },
                        {
                            title: localize("form-type-number"),
                            value: "number"
                        },
                        {
                            title: localize("form-type-phone"),
                            value: "phone"
                        },
                        {
                            title: localize("form-type-mail"),
                            value: "mail"
                        },
                        {
                            title: localize("form-type-selectbox"),
                            value: "selectbox"
                        },
                        {
                            title: localize("form-type-photo"),
                            value: "photo"
                        },
                        {
                            title: localize("form-type-yes_no"),
                            value: "yes_no"
                        }
                    ]}
                    title={localize("forms-create-question-type")}
                    titleExtractor={item => item.title}
                    keyExtractor={item => item.value}
                    spreadBehaviour="stretch"
                />
                <View
                    style={[
                        stylesheet.rowGroup,
                        {
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Switch
                        customTheme={{
                            themeKey: "events_form" as never
                        }}
                        isActive={Boolean(field.isRequired)}
                        onPress={() => updateField(index, {
                            isRequired: !field.isRequired
                        })}
                        type="neutral"
                    />
                    <View
                        style={{
                            flexDirection: "column",
                            flex: 1
                        }}
                    >
                        <Text
                            variant="bodyMediumSize"
                        >
                            {localize("forms-create-is-required")}
                        </Text>
                        <Text
                            customColor="content.text.low"
                            variant="bodySmallSize"
                        >
                            {localize("forms-create-is-required-desc")}
                        </Text>
                    </View>
                </View>
                {
                    field.type !== "yes_no" ?
                        <View
                            style={[
                                stylesheet.rowGroup,
                                {
                                    gap: spaces.spacingLg
                                }
                            ]}
                        >
                            <View
                                style={{
                                    flex: 1
                                }}
                            >
                                <TextInput
                                    value={field.minLength !== undefined ? field.minLength.toString() : ""}
                                    customColor={colors.project.inputBackground}
                                    title={localize("form-question-min-length")}
                                    onChangeText={text => updateField(index, {
                                        minLength: parseInt(text) || undefined
                                    })}
                                    spreadBehaviour="stretch"
                                    keyboardType="numeric"
                                />
                            </View>
                            <View
                                style={{
                                    flex: 1
                                }}
                            >
                                <TextInput
                                    value={field.maxLength !== undefined ? field.maxLength.toString() : ""}
                                    customColor={colors.project.inputBackground}
                                    title={localize("form-question-max-length")}
                                    onChangeText={text => updateField(index, {
                                        maxLength: parseInt(text) || undefined
                                    })}
                                    spreadBehaviour="stretch"
                                    keyboardType="numeric"
                                />
                            </View>
                        </View>
                    :
                        null
                }
                {
                    field.type === "selectbox" && (
                        <View
                            style={[
                                stylesheet.optionsContainer,
                                {
                                    marginTop: spaces.spacingMd,
                                    gap: spaces.spacingMd
                                }
                            ]}
                        >
                            <Text
                                variant="titleSmallSize"
                            >
                                {localize("form-question-options")}
                            </Text>
                            {
                                (field.options || []).map((opt, optIndex) => <View
                                    key={`opt_${index}_${optIndex}`}
                                    style={[
                                        stylesheet.optionRow,
                                        {
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                >
                                    <View
                                        style={stylesheet.optionInput}
                                    >
                                        <TextInput
                                            onChangeText={text => updateOptionInField(index, optIndex, "title", text)}
                                            placeholder={localize("form-option-title-placeholder")}
                                            customColor={colors.project.inputBackground}
                                            spreadBehaviour="stretch"
                                            value={opt.title}
                                        />
                                    </View>
                                    <View
                                        style={stylesheet.optionInput}
                                    >
                                        <TextInput
                                            onChangeText={text => updateOptionInField(index, optIndex, "key", text)}
                                            placeholder={localize("form-option-key-placeholder")}
                                            customColor={colors.project.inputBackground}
                                            spreadBehaviour="stretch"
                                            value={opt.key}
                                        />
                                    </View>
                                    <Button
                                        onPress={() => removeOptionFromField(index, optIndex)}
                                        icon={({
                                            color,
                                            size
                                        }) => <TrashIcon
                                            color={colors.content.icon[color]}
                                            size={size}
                                        />}
                                        spreadBehaviour="free"
                                        type="danger"
                                    />
                                </View>)
                            }
                            <Button
                                title={localize("form-question-add-option")}
                                onPress={() => addOptionToField(index)}
                                icon={({
                                    color,
                                    size
                                }) => <PlusIcon
                                    color={colors.content.icon[color]}
                                    size={size}
                                />}
                                spreadBehaviour="stretch"
                                type="neutral"
                            />
                            <View
                                style={[
                                    stylesheet.rowGroup,
                                    {
                                        gap: spaces.spacingMd
                                    }
                                ]}
                            >
                                <View
                                    style={{
                                        flex: 1
                                    }}
                                >
                                    <Button
                                        onPress={() => fetchStaticDataAndFill(index, "getSectors")}
                                        title={localize("forms-auto-sectors")}
                                        spreadBehaviour="stretch"
                                        variant="outline"
                                        type="primary"
                                    />
                                </View>
                                <View
                                    style={{
                                        flex: 1
                                    }}
                                >
                                    <Button
                                        onPress={() => fetchStaticDataAndFill(index, "getPositions")}
                                        title={localize("forms-auto-positions")}
                                        spreadBehaviour="stretch"
                                        variant="outline"
                                        type="primary"
                                    />
                                </View>
                            </View>
                            {
                                (field.options && field.options.length > 0) ?
                                    <Button
                                        title={localize("forms-clear-options")}
                                        onPress={() => clearOptions(index)}
                                        spreadBehaviour="stretch"
                                        variant="outline"
                                        type="neutral"
                                    />
                                :
                                    null
                            }
                        </View>)
                }
            </View>
        </View>;
    };

    const renderFields = () => {
        return <View
            style={[
                stylesheet.formGroup,
                {
                    marginTop: spaces.spacingXl
                }
            ]}
        >
            <View
                style={stylesheet.headerContainer}
            >
                <View
                    style={{
                        flexDirection: "column",
                        gap: spaces.spacingXs,
                        flex: 1
                    }}
                >
                    <Text
                        variant="titleMediumSize"
                    >
                        {localize("forms-create-questions")}
                    </Text>
                    <Text
                        customColor="content.text.low"
                        variant="bodySmallSize"
                    >
                        {localize("forms-create-questions-desc")}
                    </Text>
                </View>
            </View>
            {formData.fields.map(renderField)}
            <View
                style={{
                    marginTop: spaces.spacingLg
                }}
            >
                <Button
                    title={localize("forms-create-add-question")}
                    icon={({
                        size
                    }) => <PlusIcon
                        color={colors.project.brandWhite}
                        size={size}
                    />}
                    customBorderColor="secondaryButton"
                    customColor="secondaryButton"
                    spreadBehaviour="stretch"
                    onPress={addField}
                    variant="filled"
                />
            </View>
        </View>;
    };

    const renderFooter = () => {
        return <View
            style={[
                stylesheet.rowGroup,
                {
                    justifyContent: "space-between",
                    marginTop: spaces.spacingXl,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View>
                <Button
                    icon={({
                        color,
                        size
                    }) => <TrashIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    title={localize("forms-edit-delete")}
                    onPress={handleDelete}
                    spreadBehaviour="free"
                    isLoading={saving}
                    type="danger"
                />
            </View>
            <View
                style={{
                    flexDirection: "row",
                    gap: spaces.spacingMd
                }}
            >
                <Button
                    title={localize("forms-create-cancel")}
                    onPress={() => navigation.goBack()}
                    spreadBehaviour="free"
                    variant="filled"
                    type="neutral"
                />
                <Button
                    icon={({
                        color,
                        size
                    }) => <SaveIcon
                        color={colors.content.icon[color]}
                        size={size}
                    />}
                    title={localize("forms-create-save")}
                    spreadBehaviour="free"
                    onPress={handleSave}
                    isLoading={saving}
                    variant="filled"
                    type="primary"
                />
            </View>
        </View>;
    };

    if(loading) {
        return <PageContainer>
            <View
                style={{
                    justifyContent: "center",
                    alignItems: "center",
                    flex: 1
                }}
            >
                <ActivityIndicator
                    color={colors.project.gold}
                    size="large"
                />
            </View>
        </PageContainer>;
    }

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.cardContainer,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.project.brandBorder,
                        paddingBottom: spaces.spacingXl,
                        borderWidth: borders.seperator,
                        paddingTop: spaces.spacingXl,
                        borderRadius: radiuses.md
                    }
                ]}
            >
                <View
                    style={{
                        paddingHorizontal: spaces.spacingXl
                    }}
                >
                    {renderHeader()}
                    {renderGeneralInfo()}
                    {renderFields()}
                    {renderFooter()}
                </View>
            </View>
        </View>
        <Dialog
            primaryButtonProps={{
                title: localize("forms-create-confirm-yes"),
                onPress: handleConfirmSave,
                variant: "filled",
                type: "primary"
            }}
            onOverlayPress={() => setIsDialogVisible(false)}
            secondaryButtonProps={{
                title: localize("forms-create-confirm-no"),
                onPress: () => setIsDialogVisible(false),
                variant: "filled",
                type: "neutral"
            }}
            onClosed={() => setIsDialogVisible(false)}
            isVisible={isDialogVisible}
            variant="yes-no"
        >
            <View
                style={{
                    padding: spaces.spacingLg
                }}
            >
                <Text
                    variant="bodyMediumSize"
                >
                    {localize("forms-edit-confirm-desc")}
                </Text>
            </View>
        </Dialog>
        <Dialog
            onOverlayPress={() => setIsDeleteDialogVisible(false)}
            secondaryButtonProps={{
                onPress: () => setIsDeleteDialogVisible(false),
                title: localize("forms-delete-confirm-no"),
                customColor: "secondaryButton",
                variant: "filled"
            }}
            primaryButtonProps={{
                title: localize("forms-delete-confirm-yes"),
                icon: ({
                    color,
                    size
                }) => <TrashIcon
                    color={colors.content.icon[color]}
                    size={size}
                />,
                onPress: handleConfirmDelete,
                isLoading: saving,
                variant: "filled",
                type: "danger"
            }}
            onClosed={() => setIsDeleteDialogVisible(false)}
            isVisible={isDeleteDialogVisible}
            variant="yes-no"
        >
            <View
                style={{
                    padding: spaces.spacingLg
                }}
            >
                <Text
                    variant="bodyMediumSize"
                >
                    {localize("forms-delete-confirm-title")}
                </Text>
            </View>
        </Dialog>
    </PageContainer>;
};

export default FormEdit;
